package com.routexk.quiz.ui.localization

import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.staticCompositionLocalOf

interface AppStrings {
    // Navigation
    val navHome: String
    val navAbout: String
    val navServices: String
    val navProjects: String
    val navContacts: String

    // Common
    val appName: String
    val loading: String
    val retry: String
    val dismiss: String
    val errorGeneric: String

    // Home
    val heroTitle: String
    val heroSubtitle: String
    val heroCta: String

    // About
    val aboutTitle: String
    val aboutDescription: String

    // Services
    val servicesTitle: String

    // Projects
    val projectsTitle: String

    // Contact
    val contactTitle: String
    val contactSubtitle: String
    val contactNameLabel: String
    val contactNamePlaceholder: String
    val contactEmailLabel: String
    val contactEmailPlaceholder: String
    val contactMessageLabel: String
    val contactMessagePlaceholder: String
    val contactSend: String
    val contactSuccess: String
    val contactErrorName: String
    val contactErrorEmail: String
    val contactErrorMessage: String

    // Cookie Consent
    val cookieTitle: String
    val cookieMessage: String
    val cookieAccept: String
    val cookieDecline: String

    // Theme
    val themeLight: String
    val themeDark: String

    // Accessibility
    val ariaOpenMenu: String
    val ariaCloseMenu: String
    val ariaToggleTheme: String
    val ariaBackToTop: String

    // Footer
    val footerCopyright: String
    val footerRightsReserved: String
}

object AppStringsEN : AppStrings {
    override val navHome = "Home"
    override val navAbout = "About"
    override val navServices = "Services"
    override val navProjects = "Projects"
    override val navContacts = "Contacts"

    override val appName = "Web Template"
    override val loading = "Loading..."
    override val retry = "Retry"
    override val dismiss = "Dismiss"
    override val errorGeneric = "Something went wrong"

    override val heroTitle = "Welcome"
    override val heroSubtitle = "Your next project starts here"
    override val heroCta = "Get in Touch"

    override val aboutTitle = "About Us"
    override val aboutDescription = "We create digital experiences"

    override val servicesTitle = "Our Services"

    override val projectsTitle = "Our Projects"

    override val contactTitle = "Contact"
    override val contactSubtitle = "Have a question? We'd love to hear from you."
    override val contactNameLabel = "Name"
    override val contactNamePlaceholder = "Your name"
    override val contactEmailLabel = "Email"
    override val contactEmailPlaceholder = "your@email.com"
    override val contactMessageLabel = "Message"
    override val contactMessagePlaceholder = "How can we help?"
    override val contactSend = "Send Message"
    override val contactSuccess = "Message sent successfully!"
    override val contactErrorName = "Please enter your name"
    override val contactErrorEmail = "Please enter a valid email"
    override val contactErrorMessage = "Please enter a message"

    override val cookieTitle = "We use cookies"
    override val cookieMessage = "This website uses cookies to ensure you get the best experience."
    override val cookieAccept = "Accept"
    override val cookieDecline = "Decline"

    override val themeLight = "Light"
    override val themeDark = "Dark"

    override val ariaOpenMenu = "Open menu"
    override val ariaCloseMenu = "Close menu"
    override val ariaToggleTheme = "Toggle color theme"
    override val ariaBackToTop = "Back to top"

    override val footerCopyright = "\u00A9 2026, Your Company."
    override val footerRightsReserved = "All rights reserved."
}

object AppStringsUA : AppStrings {
    override val navHome = "\u0413\u043E\u043B\u043E\u0432\u043D\u0430"
    override val navAbout = "\u041F\u0440\u043E \u043D\u0430\u0441"
    override val navServices = "\u041F\u043E\u0441\u043B\u0443\u0433\u0438"
    override val navProjects = "\u041F\u0440\u043E\u0435\u043A\u0442\u0438"
    override val navContacts = "\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u0438"

    override val appName = "\u0412\u0435\u0431 \u0428\u0430\u0431\u043B\u043E\u043D"
    override val loading = "\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F..."
    override val retry = "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u0438"
    override val dismiss = "\u0421\u043A\u0430\u0441\u0443\u0432\u0430\u0442\u0438"
    override val errorGeneric = "\u0429\u043E\u0441\u044C \u043F\u0456\u0448\u043B\u043E \u043D\u0435 \u0442\u0430\u043A"

    override val heroTitle = "\u041B\u0430\u0441\u043A\u0430\u0432\u043E \u043F\u0440\u043E\u0441\u0438\u043C\u043E"
    override val heroSubtitle = "\u0412\u0430\u0448 \u043D\u0430\u0441\u0442\u0443\u043F\u043D\u0438\u0439 \u043F\u0440\u043E\u0435\u043A\u0442 \u043F\u043E\u0447\u0438\u043D\u0430\u0454\u0442\u044C\u0441\u044F \u0442\u0443\u0442"
    override val heroCta = "\u0417\u0432'\u044F\u0437\u0430\u0442\u0438\u0441\u044F \u0437 \u043D\u0430\u043C\u0438"

    override val aboutTitle = "\u041F\u0440\u043E \u043D\u0430\u0441"
    override val aboutDescription = "\u041C\u0438 \u0441\u0442\u0432\u043E\u0440\u044E\u0454\u043C\u043E \u0446\u0438\u0444\u0440\u043E\u0432\u0456 \u0432\u0440\u0430\u0436\u0435\u043D\u043D\u044F"

    override val servicesTitle = "\u041D\u0430\u0448\u0456 \u043F\u043E\u0441\u043B\u0443\u0433\u0438"

    override val projectsTitle = "\u041D\u0430\u0448\u0456 \u043F\u0440\u043E\u0435\u043A\u0442\u0438"

    override val contactTitle = "\u0417\u0432'\u044F\u0437\u0430\u0442\u0438\u0441\u044F \u0437 \u043D\u0430\u043C\u0438"
    override val contactSubtitle = "\u041C\u0430\u0454\u0442\u0435 \u043F\u0438\u0442\u0430\u043D\u043D\u044F? \u041C\u0438 \u0440\u0430\u0434\u0456 \u043F\u043E\u0447\u0443\u0442\u0438 \u0432\u0430\u0441."
    override val contactNameLabel = "\u0406\u043C'\u044F"
    override val contactNamePlaceholder = "\u0412\u0430\u0448\u0435 \u0456\u043C'\u044F"
    override val contactEmailLabel = "Email"
    override val contactEmailPlaceholder = "your@email.com"
    override val contactMessageLabel = "\u041F\u043E\u0432\u0456\u0434\u043E\u043C\u043B\u0435\u043D\u043D\u044F"
    override val contactMessagePlaceholder = "\u0427\u0438\u043C \u043C\u0438 \u043C\u043E\u0436\u0435\u043C\u043E \u0434\u043E\u043F\u043E\u043C\u043E\u0433\u0442\u0438?"
    override val contactSend = "\u041D\u0430\u0434\u0456\u0441\u043B\u0430\u0442\u0438"
    override val contactSuccess = "\u041F\u043E\u0432\u0456\u0434\u043E\u043C\u043B\u0435\u043D\u043D\u044F \u0443\u0441\u043F\u0456\u0448\u043D\u043E \u043D\u0430\u0434\u0456\u0441\u043B\u0430\u043D\u043E!"
    override val contactErrorName = "\u0411\u0443\u0434\u044C \u043B\u0430\u0441\u043A\u0430, \u0432\u0432\u0435\u0434\u0456\u0442\u044C \u0456\u043C'\u044F"
    override val contactErrorEmail = "\u0411\u0443\u0434\u044C \u043B\u0430\u0441\u043A\u0430, \u0432\u0432\u0435\u0434\u0456\u0442\u044C \u043A\u043E\u0440\u0435\u043A\u0442\u043D\u0438\u0439 email"
    override val contactErrorMessage = "\u0411\u0443\u0434\u044C \u043B\u0430\u0441\u043A\u0430, \u0432\u0432\u0435\u0434\u0456\u0442\u044C \u043F\u043E\u0432\u0456\u0434\u043E\u043C\u043B\u0435\u043D\u043D\u044F"

    override val cookieTitle = "\u041C\u0438 \u0432\u0438\u043A\u043E\u0440\u0438\u0441\u0442\u043E\u0432\u0443\u0454\u043C\u043E cookies"
    override val cookieMessage = "\u0426\u0435\u0439 \u0441\u0430\u0439\u0442 \u0432\u0438\u043A\u043E\u0440\u0438\u0441\u0442\u043E\u0432\u0443\u0454 cookies \u0434\u043B\u044F \u043A\u0440\u0430\u0449\u043E\u0433\u043E \u0434\u043E\u0441\u0432\u0456\u0434\u0443."
    override val cookieAccept = "\u041F\u0440\u0438\u0439\u043D\u044F\u0442\u0438"
    override val cookieDecline = "\u0412\u0456\u0434\u0445\u0438\u043B\u0438\u0442\u0438"

    override val themeLight = "\u0421\u0432\u0456\u0442\u043B\u0430"
    override val themeDark = "\u0422\u0435\u043C\u043D\u0430"

    override val ariaOpenMenu = "\u0412\u0456\u0434\u043A\u0440\u0438\u0442\u0438 \u043C\u0435\u043D\u044E"
    override val ariaCloseMenu = "\u0417\u0430\u043A\u0440\u0438\u0442\u0438 \u043C\u0435\u043D\u044E"
    override val ariaToggleTheme = "\u041F\u0435\u0440\u0435\u043C\u043A\u043D\u0443\u0442\u0438 \u0442\u0435\u043C\u0443"
    override val ariaBackToTop = "\u041F\u043E\u0432\u0435\u0440\u043D\u0443\u0442\u0438\u0441\u044F \u0432\u0433\u043E\u0440\u0443"

    override val footerCopyright = "\u00A9 2026, \u0412\u0430\u0448\u0430 \u041A\u043E\u043C\u043F\u0430\u043D\u0456\u044F."
    override val footerRightsReserved = "\u0412\u0441\u0456 \u043F\u0440\u0430\u0432\u0430 \u0437\u0430\u0445\u0438\u0449\u0435\u043D\u0456."
}

enum class Language(val code: String, val strings: AppStrings) {
    ENGLISH("en", AppStringsEN),
    UKRAINIAN("ua", AppStringsUA);

    companion object {
        fun fromCode(code: String?): Language {
            return values().find { it.code == code } ?: ENGLISH
        }
    }
}

val LocalAppStrings = staticCompositionLocalOf<AppStrings> { AppStringsEN }
